import 'reflect-metadata';
import axios from 'axios';
import moment from '@gid/vue-common/app-settings/moment';
import Vue from 'vue';
import VueI18n from 'vue-i18n';
import VueMoment from 'vue-moment';
import * as Sentry from '@sentry/browser';

import App from './App.vue';

import messages from './i18n/messages';
import router, { holdingLandingRoute } from './router';
import store from './store';
import {
  initVeeValidateRulesWithI18n,
  setVeeValidateLocale,
} from './app/components';

import './app/filters';

import AccessibilityPlugin from '@gid/vue-common/plugins/accessibility.plugin';

import {
  PURGE_AUTH,
  STORE_USER_DATA,
} from '@gid/vue-common/store/auth.module/types';

import { e2e } from '@gid/vue-common/app-settings/customDirectives';
import { adjustTypeOrmTypesForSqlite } from '@gid/vue-common/app-settings/typeorm-util';
import { setupGoogleAnalytics } from '@gid/vue-common/app-settings/googleAnalytics';
import '@gid/vue-common/app-settings/registerServiceWorker';
import { createConnection } from 'typeorm';
import {
  ALL_ENTITIES,
  DataUiBlueprints,
  isConfinedProfileSet,
} from '@gid/models';
import { getEnv } from '@gid/vue-common/utils';

import '@/assets/scss/styles.scss';

Vue.use(VueI18n);
Vue.use(VueMoment, { moment });
Vue.use(AccessibilityPlugin, { store });

// Set app identifier for template-based context injection
Vue.prototype.$appId = 'admin';

const defaultLocale = store.getters.locale;
const i18n = new VueI18n({
  locale: defaultLocale,
  fallbackLocale: 'en',
  silentFallbackWarn: true,
  messages,
});
moment.locale(defaultLocale);
setVeeValidateLocale(defaultLocale);
initVeeValidateRulesWithI18n(i18n);

// Contacts confined to a holding profile have no access to /data-api at all —
// gid-crud's ConfinedProfileGuard 403s the whole surface. A dozen components
// across the admin app fetch from it on `created()` without a `.catch()`, so
// letting those requests go out means a wall of console errors and unhandled
// rejections on pages the holding user is legitimately allowed to see.
//
// Stubbing the adapter (rather than rejecting) keeps every caller on its happy
// path: they get an empty result and render an empty filter, which is the honest
// outcome. Scoped to confined profiles, so normal users still see real failures.
axios.interceptors.request.use((config) => {
  if (
    isConfinedProfileSet(store.getters.contactProfiles) &&
    (config.url || '').startsWith('/data-api/')
  ) {
    config.adapter = () =>
      Promise.resolve({
        data: [],
        status: 204,
        statusText: 'No Content',
        headers: {},
        config,
      });
  }
  return config;
});

router.beforeEach((to, from, next) => {
  if (!store.getters.isAuthenticated && to.name != 'login') {
    next({ name: 'login' });
  } else if (
    to.name === 'home' &&
    isConfinedProfileSet(store.getters.contactProfiles)
  ) {
    // Home is open to any authenticated user, but a holding contact has no
    // business there — every panel on it would 403. Send them to whichever
    // holding page their share link named, or the default.
    next(holdingLandingRoute());
  } else {
    next();
  }
});

store.subscribe(({ type }) => {
  if (type === PURGE_AUTH && router.currentRoute.name != 'login') {
    // Go to login page on logout
    router.push({ name: 'login' });
  } else if (type === STORE_USER_DATA) {
    // Set locales in used components
    const locale = store.getters.locale;
    i18n.locale = locale;
    moment.locale(locale);
    setVeeValidateLocale(locale);
  }
});

Vue.directive('e2e', e2e);

setupGoogleAnalytics(router);

new Vue({
  router,
  store,
  i18n,
  render: (h) => h(App),
}).$mount('#app');

// register a bogus connection so we use the metadata
adjustTypeOrmTypesForSqlite();
createConnection({
  name: 'default',
  type: 'sqljs',
  entities: Object.values(ALL_ENTITIES),
}).then((connection) => {
  DataUiBlueprints.init('/data-api/', connection);
});

if (getEnv('NODE_ENV') == 'production') {
  Sentry.init({
    dsn: 'https://8d59cfd213854345aba73ce3cad681f1@sentry.io/1384787',
    environment: getEnv('GID_ENV') ?? getEnv('VUE_APP_ENV'),
    release: getEnv('VUE_APP_SOURCE_VERSION'),
    integrations: [new Sentry.Integrations.Vue({ Vue })],
  });
}
